import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  VolumeOff as SilenceIcon,
  Timer as TimerIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Silence } from '../../../../../api/cansense/silenceApi';
import { getCardBorder, CARD_BORDER_RADIUS } from '../types';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format milliseconds into a human-readable time remaining string
 */
export const formatTimeRemaining = (ms: number): string => {
  if (ms <= 0) return 'Expired';

  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Format a date into a "time ago" string
 */
export const formatTimeAgo = (date: Date): string => {
  const ms = Date.now() - date.getTime();
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));

  if (hours > 0) {
    return `${hours}h ${minutes}m ago`;
  }
  return `${minutes}m ago`;
};

/**
 * Get countdown color based on time remaining
 * - primary (blue): > 30 minutes
 * - warning (orange): 5-30 minutes
 * - error (red): < 5 minutes
 */
export const getCountdownColor = (remainingMs: number): 'primary' | 'warning' | 'error' => {
  const fiveMinutes = 5 * 60 * 1000;
  const thirtyMinutes = 30 * 60 * 1000;

  if (remainingMs <= fiveMinutes) return 'error';
  if (remainingMs <= thirtyMinutes) return 'warning';
  return 'primary';
};

// ============================================================================
// Component
// ============================================================================

export interface SilenceCardProps {
  silence: Silence;
  onExtend?: (id: number) => void;
  onDelete?: (id: number) => void;
}

/**
 * SilenceCard - Displays a single silence with countdown timer and actions.
 *
 * Features:
 * - Color-coded left border based on time remaining (blue > orange > red)
 * - Progress bar showing elapsed time
 * - Countdown timer showing time until expiry
 * - Extend and Delete action buttons
 */
export function SilenceCard({ silence, onExtend, onDelete }: SilenceCardProps) {
  const theme = useTheme();

  // Tick counter to force re-render every 30 seconds for countdown accuracy
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate time remaining and progress (recalculates on silence change or tick)
  const { remainingMs, progressPercent, countdownColor, displayTarget } = useMemo(() => {
    const now = Date.now();
    const endsAt = new Date(silence.endsAt).getTime();
    const startsAt = new Date(silence.startsAt).getTime();
    const remaining = endsAt - now;
    const totalDuration = endsAt - startsAt;
    const elapsedMs = now - startsAt;
    const progress = Math.min(100, Math.max(0, (elapsedMs / totalDuration) * 100));

    // Display target - either exporter name or matcher expression
    const target =
      silence.exporterName ||
      silence.matchers.map((m) => `${m.name}="${m.value}"`).join(', ') ||
      'All alerts';

    return {
      remainingMs: remaining,
      progressPercent: progress,
      countdownColor: getCountdownColor(remaining),
      displayTarget: target,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silence, tick]);

  return (
    <Paper
      sx={{
        p: 2,
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
        borderLeft: `4px solid ${theme.palette[countdownColor].main}`,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: theme.shadows[2],
        },
      }}
    >
      {/* Header Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SilenceIcon sx={{ color: theme.palette[countdownColor].main, fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            {displayTarget}
          </Typography>
        </Box>

        {/* Countdown */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimerIcon sx={{ fontSize: 16, color: theme.palette[countdownColor].main }} />
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: theme.palette[countdownColor].main }}
          >
            {remainingMs > 0 ? `Expires in ${formatTimeRemaining(remainingMs)}` : 'Expired'}
          </Typography>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          color={countdownColor}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(theme.palette[countdownColor].main, 0.1),
          }}
        />
      </Box>

      {/* Reason */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Reason: {silence.comment || 'No comment provided'}
      </Typography>

      {/* Footer Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          Created by: {silence.createdBy} • Started{' '}
          {formatTimeAgo(new Date(silence.startsAt))}
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ScheduleIcon sx={{ fontSize: 16 }} />}
            onClick={() => onExtend?.(silence.id)}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
            }}
          >
            Extend
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
            onClick={() => onDelete?.(silence.id)}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default SilenceCard;
